import { getApi, postApi } from "../pages/AppApis";

/**
 * Geocoding service that handles address searches through backend proxy
 * to avoid direct browser calls to Nominatim which cause CORS and rate limiting issues
 */

/**
 * Normalize and preprocess address query for better geocoding results
 * @param {string} query - Raw address query
 * @returns {string} - Normalized query
 */
const normalizeAddressQuery = (query) => {
    if (!query) return "";

    let normalized = query.trim();

    // Handle comma-separated format: "1234, Market Street, San Francisco, San Francisco County, California, 94103, United States"
    if (normalized.includes(",")) {
        const parts = normalized
            .split(",")
            .map((part) => part.trim())
            .filter((part) => part.length > 0);

        // Remove "United States" if present
        if (parts[parts.length - 1]?.toLowerCase() === "united states") {
            parts.pop();
        }

        // Remove county if it duplicates city name (e.g., "San Francisco County" when city is "San Francisco")
        if (parts.length >= 4) {
            const city = parts[2]?.toLowerCase();
            const county = parts[3]?.toLowerCase();
            if (county && city && county.includes(city)) {
                parts.splice(3, 1); // Remove county
            }
        }

        // Reconstruct as: "number street, city, state zip"
        if (parts.length >= 3) {
            const streetParts = [];
            const cityIndex = Math.max(2, parts.length - 3); // City is usually 3rd from end (city, state, zip)

            // Combine number and street name without comma
            for (let i = 0; i < cityIndex; i++) {
                streetParts.push(parts[i]);
            }
            const street = streetParts.join(" ").replace(/,$/, ""); // Remove trailing comma

            // Get city, state, zip
            const city = parts[cityIndex] || "";
            const state = parts[cityIndex + 1] || "";
            const zip = parts[cityIndex + 2] || "";

            // Reconstruct query
            normalized = `${street}, ${city}, ${state} ${zip}`.trim();
        }
    }

    // Clean up extra spaces and normalize punctuation
    normalized = normalized
        .replace(/\s+/g, " ") // Multiple spaces to single space
        .replace(/,\s*,/g, ",") // Multiple commas to single comma
        .replace(/,\s*$/, "") // Remove trailing comma
        .trim();

    return normalized;
};

/**
 * Search for addresses using fallback to direct Nominatim with proper normalization
 * @param {string} query - The address query string
 * @param {number} limit - Maximum number of results (default: 5)
 * @returns {Promise<Array>} - Array of geocoded results
 */
export const searchAddresses = async (query, limit = 5) => {
    try {
        if (!query || query.trim().length < 3) {
            return [];
        }

        // Normalize the query first
        const normalizedQuery = normalizeAddressQuery(query);
        console.log("Original query:", query);
        console.log("Normalized query:", normalizedQuery);

        // Try backend endpoint first (when available)
        try {
            const response = await getApi(`/geocoding/search?q=${encodeURIComponent(normalizedQuery)}&limit=${limit}`);

            if (response.response && Array.isArray(response.response) && response.response.length > 0) {
                return response.response.map((item) => ({
                    place_id: item.place_id || item.id,
                    description: item.display_name || item.formatted_address,
                    lat: parseFloat(item.lat || item.latitude),
                    lng: parseFloat(item.lon || item.lng || item.longitude),
                    address: item.address || extractAddressComponents(item),
                    source: item.source || "backend",
                }));
            }
        } catch (backendError) {
            console.log("Backend geocoding not available, using fallback");
        }

        // Fallback to direct Nominatim with proper headers and normalization
        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=${limit}&countrycodes=us&q=${encodeURIComponent(normalizedQuery)}`,
            {
                headers: {
                    "User-Agent": "Portal-Real-Estate-App/1.0 (support@airebrokers.com)",
                },
            }
        );

        if (!response.ok) {
            console.warn(`Nominatim returned ${response.status}: ${response.statusText}`);

            // If normalized query fails, try with original query as backup
            if (normalizedQuery !== query.trim()) {
                console.log("Trying with original query format...");
                const fallbackResponse = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=${limit}&countrycodes=us&q=${encodeURIComponent(query.trim())}`,
                    {
                        headers: {
                            "User-Agent": "Portal-Real-Estate-App/1.0 (support@airebrokers.com)",
                        },
                    }
                );

                if (fallbackResponse.ok) {
                    const fallbackData = await fallbackResponse.json();
                    return transformNominatimResults(fallbackData);
                }
            }

            return [];
        }

        const data = await response.json();

        // If no results with normalized query, try alternative strategies
        if (!data || data.length === 0) {
            console.log("No results with normalized query, trying alternative strategies...");

            // Strategy 1: Try with just street and city
            const alternativeQueries = generateAlternativeQueries(query);

            for (const altQuery of alternativeQueries) {
                console.log("Trying alternative query:", altQuery);

                try {
                    const altResponse = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=${limit}&countrycodes=us&q=${encodeURIComponent(altQuery)}`,
                        {
                            headers: {
                                "User-Agent": "Portal-Real-Estate-App/1.0 (support@airebrokers.com)",
                            },
                        }
                    );

                    if (altResponse.ok) {
                        const altData = await altResponse.json();
                        if (altData && altData.length > 0) {
                            console.log(`Found ${altData.length} results with alternative query`);
                            return transformNominatimResults(altData);
                        }
                    }
                } catch (altError) {
                    console.warn("Alternative query failed:", altError);
                    continue;
                }

                // Add delay between requests to respect rate limits
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
        }

        return transformNominatimResults(data);
    } catch (error) {
        console.error("Error in geocoding service:", error);
        return [];
    }
};

/**
 * Generate alternative query strategies when the main query fails
 * @param {string} originalQuery - The original address query
 * @returns {Array<string>} - Array of alternative queries to try
 */
const generateAlternativeQueries = (originalQuery) => {
    const alternatives = [];

    if (originalQuery.includes(",")) {
        const parts = originalQuery
            .split(",")
            .map((part) => part.trim())
            .filter((part) => part.length > 0);

        // Strategy 1: Remove "United States" and try
        const withoutCountry = parts.filter((part) => part.toLowerCase() !== "united states").join(", ");
        if (withoutCountry !== originalQuery) {
            alternatives.push(withoutCountry);
        }

        // Strategy 2: Just number + street + city + state (no county, no zip)
        if (parts.length >= 4) {
            const streetNumber = parts[0];
            const streetName = parts[1];
            const city = parts[2];
            const stateOrCounty = parts[3];

            // If 4th part looks like a county (contains "County"), use 5th part as state
            const state = stateOrCounty.toLowerCase().includes("county") && parts[4] ? parts[4] : stateOrCounty;

            alternatives.push(`${streetNumber} ${streetName}, ${city}, ${state}`);
        }

        // Strategy 3: Just street and city
        if (parts.length >= 3) {
            const streetNumber = parts[0];
            const streetName = parts[1];
            const city = parts[2];

            alternatives.push(`${streetNumber} ${streetName}, ${city}`);
        }

        // Strategy 4: Try without the leading number (just street name + city)
        if (parts.length >= 3) {
            const streetName = parts[1];
            const city = parts[2];

            alternatives.push(`${streetName}, ${city}`);
        }
    }

    return alternatives;
};

/**
 * Transform Nominatim results to consistent format
 * @param {Array} data - Raw Nominatim results
 * @returns {Array} - Transformed results
 */
const transformNominatimResults = (data) => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => ({
        place_id: item.place_id,
        description: item.display_name,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        address: item.address || {},
        source: "nominatim",
    }));
};

/**
 * Fallback function to extract address components from various response formats
 * @param {Object} item - Geocoding result item
 * @returns {Object} - Standardized address components
 */
const extractAddressComponents = (item) => {
    // Handle different possible response formats
    if (item.address_components) {
        // Google-style format
        const components = {};
        item.address_components.forEach((comp) => {
            if (comp.types.includes("street_number")) components.house_number = comp.long_name;
            if (comp.types.includes("route")) components.road = comp.long_name;
            if (comp.types.includes("locality")) components.city = comp.long_name;
            if (comp.types.includes("administrative_area_level_1")) components.state = comp.short_name;
            if (comp.types.includes("postal_code")) components.postcode = comp.long_name;
            if (comp.types.includes("country")) components.country = comp.long_name;
        });
        return components;
    }

    // Return empty object if no recognizable format
    return {};
};

/**
 * Validate if an address is within the US
 * @param {string} address - Address string to validate
 * @returns {Promise<boolean>} - True if address is in US
 */
export const validateUSAddress = async (address) => {
    try {
        const results = await searchAddresses(address, 1);
        if (results.length > 0) {
            const result = results[0];
            // Check if address has US country component
            return (
                result.address?.country?.toLowerCase().includes("united states") ||
                result.address?.country?.toLowerCase() === "us" ||
                result.description?.toLowerCase().includes("united states")
            );
        }
        return false;
    } catch (error) {
        console.error("Error validating US address:", error);
        return false;
    }
};

/**
 * Reverse geocode coordinates to get address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object|null>} - Address information
 */
export const reverseGeocode = async (lat, lng) => {
    try {
        // Try backend endpoint first (when available)
        try {
            const response = await getApi(`/geocoding/reverse?lat=${lat}&lng=${lng}`);

            if (response.response) {
                return {
                    display_name: response.response.display_name || response.response.formatted_address,
                    address: response.response.address || extractAddressComponents(response.response),
                    source: "backend",
                };
            }
        } catch (backendError) {
            console.log("Backend reverse geocoding not available, using fallback");
        }

        // Fallback to direct Nominatim reverse geocoding
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`, {
            headers: {
                "User-Agent": "Portal-Real-Estate-App/1.0 (support@airebrokers.com)",
            },
        });

        if (!response.ok) {
            console.warn(`Nominatim reverse geocoding returned ${response.status}: ${response.statusText}`);
            return null;
        }

        const data = await response.json();

        if (data && data.display_name) {
            return {
                display_name: data.display_name,
                address: data.address || {},
                source: "nominatim",
            };
        }

        return null;
    } catch (error) {
        console.error("Error in reverse geocoding:", error);
        return null;
    }
};

/**
 * Get detailed information for a specific place
 * @param {string} placeId - The place ID to get details for
 * @returns {Promise<Object|null>} - Detailed place information
 */
export const getPlaceDetails = async (placeId) => {
    try {
        const response = await getApi(`/geocoding/details?place_id=${encodeURIComponent(placeId)}`);

        if (response.error) {
            console.error("Place details API error:", response.error);
            return null;
        }

        return response.response || null;
    } catch (error) {
        console.error("Error getting place details:", error);
        return null;
    }
};

export default {
    searchAddresses,
    validateUSAddress,
    reverseGeocode,
    getPlaceDetails,
};
